import data from '../data/foodStickers.json';

// Food stickers: turn ingredient names (or a dish hint) into the 1–3 stickers that picture a meal.
// Images live in the public `food-stickers` bucket; REACT_APP_STICKER_BASE_URL points at its version folder.
// No base URL → no stickers (the UI simply shows none).

const BASE = (process.env.REACT_APP_STICKER_BASE_URL || '').replace(/\/+$/, '');
const bySlug = new Map(data.stickers.map((s) => [s.slug, s]));

// `rev` is set when an image was replaced, so browsers and the CDN fetch the new one instead of the cached file.
export const stickerUrl = (sticker) => (BASE && sticker ? `${BASE}/${sticker.slug}.${sticker.ext}${sticker.rev ? `?v=${sticker.rev}` : ''}` : null);
export const stickersEnabled = () => !!BASE;

// Other ways recipes name a sticker's food. Each sticker's English and Hebrew names are always included.
const ALIASES = {
  oats: ['oatmeal', 'rolled oats', 'oat flakes', 'porridge', 'quick oats', 'דייסת שיבולת שועל'],
  'white-rice': ['rice', 'basmati rice', 'jasmine rice', 'אורז'],
  pasta: ['spaghetti', 'penne', 'macaroni', 'fusilli', 'noodles', 'ספגטי'],
  'whole-wheat-bread': ['whole grain bread', 'wholemeal bread', 'multigrain bread', 'rye bread', 'לחם חיטה מלאה', 'לחם מלא'],
  'white-bread': ['bread', 'toast', 'sourdough', 'לחם'],
  pita: ['pita bread', 'פיטה'],
  tortilla: ['wrap', 'lavash', 'לאפה'],
  'rice-cakes': ['rice cake', 'corn cakes', 'פריכיות'],
  granola: ['muesli'],
  cornflakes: ['cereal'],
  corn: ['sweet corn', 'corn kernels'],
  clementine: ['mandarin', 'tangerine'],
  peach: ['nectarine'],
  melon: ['cantaloupe', 'honeydew'],
  lemon: ['lime'],
  'dried-cranberries': ['cranberries', 'craisins'],
  lettuce: ['romaine', 'iceberg', 'mixed greens', 'salad greens'],
  arugula: ['rocket'],
  cabbage: ['coleslaw'],
  'bell-pepper': ['red pepper', 'green pepper', 'yellow pepper', 'capsicum', 'sweet pepper', 'פלפלים', 'פלפלים אדומים'],
  onion: ['shallot', 'scallion', 'green onion', 'spring onion'],
  zucchini: ['courgette', 'קישואים'],
  eggplant: ['aubergine'],
  'sweet-potato': ['yam'],
  pumpkin: ['butternut squash', 'squash'],
  beetroot: ['beet', 'beets'],
  'green-beans': ['string beans', 'yellow beans', 'שעועית צהובה', 'שעועית ירוקה'],
  peas: ['green peas'],
  pickles: ['pickle', 'pickled cucumber'],
  chickpeas: ['garbanzo', 'garbanzo beans'],
  'kidney-beans': ['red beans'],
  'white-beans': ['cannellini', 'navy beans', 'butter beans', 'שעועית לבנה'],
  tahini: ['tahina', 'sesame paste', 'טחינה גולמית'],
  flaxseeds: ['flax', 'linseed', 'ground flaxseed'],
  'pumpkin-seeds': ['pepitas'],
  milk: ['whole milk', 'skim milk', 'low fat milk'],
  yogurt: ['yoghurt', 'plain yogurt'],
  'greek-yogurt': ['skyr', 'greek yoghurt', 'pro yogurt'],
  'cottage-cheese': ['cottage'],
  'white-cheese': ['quark', 'soft white cheese'],
  'feta-cheese': ['feta', 'bulgarian cheese', 'safed cheese', 'tzfatit'],
  'yellow-cheese': ['gouda', 'edam', 'emek', 'sliced cheese', 'cheese slice', 'cheese yellow'],
  'cheddar-cheese': ['cheddar'],
  parmesan: ['parmigiano'],
  egg: ['eggs', 'whole egg', 'large eggs', 'ביצים'],
  'boiled-egg': ['hard boiled egg', 'hard boiled', 'egg boiled'],
  'fried-egg': ['sunny side up', 'sunny side up eggs'],
  omelette: ['omelet', 'scrambled eggs', 'scrambled egg'],
  'egg-whites': ['egg white'],
  'chicken-thigh': ['chicken thighs', 'pargit', 'פרגית'],
  'turkey-slices': ['deli turkey', 'turkey pastrami', 'pastrami'],
  'beef-steak': ['steak', 'sirloin', 'entrecote', 'beef', 'red meat'],
  'ground-beef': ['minced beef', 'beef mince', 'ground meat', 'minced meat'],
  meatballs: ['kofta', 'kebab'],
  'smoked-salmon': ['lox'],
  'canned-tuna': ['tuna in water', 'tuna canned', 'canned tuna in water', 'tuna salad'],
  tilapia: ['musht'],
  'sea-bass': ['bass', 'sea bream', 'denis'],
  shrimp: ['prawns', 'prawn'],
  olives: ['olive'],
  'date-syrup': ['silan'],
  jam: ['jelly', 'preserves', 'fruit spread'],
  cookies: ['cookie', 'biscuit', 'biscuits'],
  coffee: ['espresso', 'latte', 'cappuccino', 'americano'],
  'whey-protein-powder': ['whey', 'protein powder', 'casein', 'gainer', 'gainer powder', 'protein isolate', 'soy protein isolate', 'אבקת חלבון מי גבינה', 'חלבון מי גבינה', 'איזולוט'],
  'protein-shake': ['protein drink'],
  mayonnaise: ['mayo'],
  'balsamic-vinegar': ['balsamic'],
  // Hebrew product words from the food database (ingridientsroee).
  'chicken-breast': ['chicken', 'chicken fillet', 'grilled chicken', 'עוף', 'בשר עוף', 'פילה עוף', 'חזה עוף'],
  'turkey-breast': ['turkey', 'הודו', 'בשר הודו'],
  potato: ['potatoes', 'תפוחי אדמה', 'פירה'],
  strawberries: ['תות שדה', 'תות', 'תותים'],
  'soy-milk': ['soy drink', 'משקה סויה'],
  'almond-milk': ['almond drink', 'משקה שקדים'],
  'oat-milk': ['oat drink', 'משקה שיבולת שועל'],
  'dark-chocolate': ['chocolate', 'cocoa', 'שוקולד', 'שוקולד מריר', 'קקאו'],
  // Seen in saved plans: Hebrew plurals, machine-translation slips ("מותק" for honey) and brand names.
  tomato: ['עגבניות'],
  cucumber: ['מלפפונים'],
  honey: ['מותק'],
  apple: ['אפל'],
  bulgur: ['בורגור'],
  crackers: ['wheat thins'],
  'protein-bar': ['חלבון חטיף'],
};

// Phrases that name a food we have no sticker for, but would otherwise match a wrong one ("tomato sauce" ≠ tomato).
const GROUP_PHRASES = {
  sauces: ['tomato sauce', 'tomato paste', 'marinara', 'pasta sauce', 'vinaigrette', 'salad dressing', 'רוטב עגבניות'],
  dairy: ['sour cream', 'cream cheese spread'],
};

// Never pictured: seasonings, herbs, liquids and cooking fats. Matched like foods, so "lemon juice" beats "lemon".
const SKIP = [
  'salt', 'sea salt', 'black pepper', 'ground pepper', 'pepper', 'water', 'ice', 'ice cubes', 'lemon juice', 'lime juice',
  'oil', 'canola oil', 'vegetable oil', 'sunflower oil', 'cooking spray', 'oil spray', 'olive oil spray',
  'parsley', 'dill', 'cilantro', 'coriander', 'mint', 'basil', 'oregano', 'thyme', 'rosemary', 'sage', 'herbs', 'chives',
  'cinnamon', 'cumin', 'paprika', 'turmeric', 'curry powder', 'chili flakes', 'chili powder', 'sumac', 'zaatar', 'za atar',
  'garlic powder', 'onion powder', 'spices', 'seasoning', 'vanilla', 'vanilla extract', 'baking powder', 'baking soda', 'yeast',
  'sweetener', 'stevia', 'sugar', 'salt and pepper', 'flour', 'wheat flour', 'whole wheat flour', 'all purpose flour', 'cornstarch', 'corn starch',
  'dill weed', 'canola oil', 'שמן', 'שמן קנולה', 'מלח', 'פלפל שחור', 'מים', 'מיץ לימון', 'פטרוזיליה', 'שמיר', 'דיל', 'כוסברה', 'נענע', 'קינמון', 'כמון', 'פפריקה', 'כורכום', 'סוכר', 'קמח',
];
// Pictured if asked for directly, but a cooking fat never stands for a meal.
const SKIP_IN_MEALS = new Set(['olive-oil', 'coconut-oil']);

// Last resort for a single ingredient: a word that names its food group.
const CATEGORY_WORDS = {
  grains: ['grain', 'grains', 'cereal', 'barley', 'wheat', 'flour', 'oat'],
  bread: ['bun', 'roll', 'baguette', 'croissant', 'cracker'],
  fruit: ['fruit', 'berries', 'berry', 'פירות'],
  vegetables: ['vegetable', 'vegetables', 'veggies', 'salad', 'greens', 'leaves', 'sprouts', 'ירקות', 'סלט'],
  legumes: ['bean', 'beans', 'lentil', 'legumes', 'קטניות'],
  'nuts-seeds': ['nut', 'nuts', 'seed', 'seeds', 'אגוזים'],
  dairy: ['cheese', 'cream', 'dairy', 'גבינה'],
  eggs: ['egg'],
  meat: ['meat', 'veal', 'pork', 'burger', 'sausage', 'בשר'],
  poultry: ['duck', 'poultry', 'עוף'],
  fish: ['fish', 'seafood', 'crab', 'דג'],
  'oils-fats': ['ghee', 'margarine'],
  sweets: ['candy', 'cake', 'dessert', 'syrup', 'sweet', 'עוגה'],
  drinks: ['juice', 'drink', 'soda', 'מיץ'],
  sauces: ['sauce', 'dressing', 'dip', 'spread', 'vinegar', 'vinaigrette', 'רוטב'],
};

const HEBREW = /[֐-׿]/;
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function normalize(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/['’`]/g, ' ')
    .replace(/[^a-z0-9֐-׿]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

// Singular/plural variants of an English phrase (last word only).
function variants(phrase) {
  const out = new Set([phrase]);
  if (HEBREW.test(phrase)) return [...out];
  const m = phrase.match(/^(.*?)([a-z]+)$/);
  if (!m) return [...out];
  const [, head, last] = m;
  const add = (w) => out.add(head + w);
  if (last.endsWith('ies')) add(`${last.slice(0, -3)}y`);
  else if (/[^aeiou]y$/.test(last)) add(`${last.slice(0, -1)}ies`);
  if (last.endsWith('oes') || last.endsWith('ches') || last.endsWith('shes')) add(last.slice(0, -2));
  else if (last.endsWith('s') && !last.endsWith('ss')) add(last.slice(0, -1));
  else { add(`${last}s`); if (/(o|ch|sh)$/.test(last)) add(`${last}es`); }
  return [...out];
}

// One index of every phrase → what it means, longest phrase first, so "almond milk" wins over "almond".
function buildIndex(entries) {
  const seen = new Set();
  const index = [];
  for (const { phrase, value } of entries) {
    for (const v of variants(normalize(phrase))) {
      if (!v || seen.has(v)) continue;
      seen.add(v);
      // Hebrew words take one-letter prefixes (ו, ה, ב…), so allow up to two before the phrase.
      const prefix = HEBREW.test(v) ? '[והבלמשכ]{0,2}' : '';
      index.push({ phrase: v, value, re: new RegExp(`(?:^| )${prefix}${escapeRe(v)}(?= |$)`) });
    }
  }
  return index.sort((a, b) => b.phrase.length - a.phrase.length);
}

// Order matters when two entries share a phrase: seasonings first, then real stickers (names, aliases written here,
// and aliases that came with a sticker batch), then group stand-ins — so a new "Tomato sauce" sticker replaces the stand-in.
const FOOD_INDEX = buildIndex([
  ...SKIP.map((phrase) => ({ phrase, value: { skip: true } })),
  ...data.stickers.filter((s) => s.kind === 'food' && !s.slug.endsWith('-icon')).flatMap((s) => [
    ...[s.en, s.he, ...(ALIASES[s.slug] || []), ...(s.aliases || [])].filter(Boolean).map((phrase) => ({ phrase, value: { slug: s.slug } })),
  ]),
  ...Object.entries(GROUP_PHRASES).flatMap(([cat, phrases]) => phrases.map((phrase) => ({ phrase, value: { slug: `category-${cat}`, group: true } }))),
]);
const CATEGORY_INDEX = buildIndex(
  Object.entries(CATEGORY_WORDS).flatMap(([cat, words]) => words.map((phrase) => ({ phrase, value: { slug: `category-${cat}` } }))),
);

const withUrl = (sticker) => (sticker ? { ...sticker, url: stickerUrl(sticker) } : null);

// Meal-type icons by slot name, English or Hebrew ("Breakfast icon" → "breakfast", "ארוחת בוקר").
const MEAL_ICONS = new Map();
data.stickers.filter((st) => st.slug.endsWith('-icon')).forEach((st) => {
  MEAL_ICONS.set(normalize(String(st.en || '').replace(/ icon$/i, '')), st);
  if (st.he) MEAL_ICONS.set(normalize(st.he), st);
});

/** The general picture for a meal slot (Breakfast, Lunch, …), or null when there's none uploaded yet. */
export const mealIcon = (slotName) => withUrl(MEAL_ICONS.get(normalize(slotName)) || null);

// What kind of product it is. When the main part names its type, the type decides the picture: "Pear Banana
// Nectar" is a drink, not a banana. A longer name that includes the type still wins ("orange juice", "cream cheese").
const PRODUCT_TYPES = {
  'category-drinks': ['juice', 'nectar', 'soda', 'carbonated', 'cola', 'soft drink', 'drink', 'beverage', 'wine', 'beer', 'liqueur',
    'מיץ', 'נקטר', 'סודה', 'משקה', 'יין', 'בירה'],
  smoothie: ['shake', 'שייק'],
  'category-sweets': ['pudding', 'jelly', 'chips', 'crisps', 'snack', 'puffs', 'cake', 'muffin', 'candy', 'wafer', 'wafers', 'halva',
    'פודינג', 'מעדן', 'צ׳יפס', 'צ\'יפס', 'חטיף', 'עוגה', 'ופל', 'סוכריות'],
  cookies: ['cookie', 'cookies', 'biscuit', 'biscuits', 'עוגיות', 'ביסקוויט', 'ביסקוויטים'],
  crackers: ['cracker', 'crackers', 'קרקר', 'קרקרים'],
  'white-bread': ['bread', 'roll', 'bun', 'baguette', 'croissant', 'pastry', 'לחם', 'לחמניה', 'לחמנייה', 'מאפה'],
  cornflakes: ['cereal', 'flakes', 'דגני בוקר'],
  'protein-bar': ['bar'],
  'category-sauces': ['sauce', 'dressing', 'spread', 'dip', 'רוטב', 'ממרח'],
  'category-vegetables': ['soup', 'broth', 'מרק'],
  yogurt: ['yogurt', 'yoghurt', 'יוגורט'],
  'category-dairy': ['cheese', 'גבינה'],
};
const TYPE_INDEX = buildIndex(Object.entries(PRODUCT_TYPES).flatMap(([slug, words]) => words.map((phrase) => ({ phrase, value: { slug, group: slug.startsWith('category-') } }))));

// Words that describe a product without being a food ("sugar free" isn't sugar).
const NOT_A_FOOD = ['sugar free', 'no sugar added', 'no added sugar', 'less sugar', 'reduced sugar', 'low fat', 'fat free', 'low sugar',
  'ללא סוכר', 'ללא תוספת סוכר', 'דל שומן', 'מופחת סוכר'];

// Store products read "Brand - Main thing with extras, Maker". The main thing decides the picture, so each
// " - " part is cut where its extras start ("Special Bread with Olive Oil" → "special bread"). Flavours don't count:
// "pineapple flavored", "flavored with chocolate" and "בטעם תות" are dropped.
const EXTRAS_START = ['with', 'made from', 'made of', 'topped with', 'filled with', 'stuffed with', 'served with', 'from', 'for', 'in',
  'flavored with', 'flavoured with', 'עם', 'במילוי', 'בתוספת', 'בטעם', 'ברוטב'];
// A cut word that's part of a known name ("tuna in water") doesn't cut there.
const cutIsInsideKnownName = (text, word) => FOOD_INDEX.some((e) => e.phrase.includes(` ${word} `) && e.re.test(text.trim()));
function mainParts(name) {
  return String(name || '')
    .split(/\s[-–—]\s/)
    .map((part) => {
      let text = ` ${normalize(part)} `;
      NOT_A_FOOD.forEach((p) => { text = text.split(` ${p} `).join(' '); });
      // "pineapple flavored" / "chocolate flavor": the word before is a flavour, not the product.
      text = text.replace(/ [a-z]+ flavou?r(ed)? /g, ' ').replace(/ flavou?r(ed)? /g, ' ');
      const padded = ` ${text.trim()} `;
      const cut = EXTRAS_START
        .filter((w) => !cutIsInsideKnownName(padded, w))
        .map((w) => padded.indexOf(` ${w} `))
        .filter((i) => i > 0)
        .reduce((a, b) => Math.min(a, b), padded.length);
      return padded.slice(0, cut).trim();
    })
    .filter(Boolean);
}

// The best match inside one main part:
//   1. a product type ("nectar", "cookies") decides, unless a longer known name includes it ("orange juice", "lemon juice")
//   2. otherwise the longest name, but a cooking oil only when nothing else matched ("pastrami in olive oil" → pastrami)
function bestHit(part) {
  const hits = FOOD_INDEX.filter((e) => e.re.test(part));
  const type = TYPE_INDEX.find((e) => e.re.test(part));
  if (type) return hits.find((h) => h.phrase.includes(type.phrase) && h.phrase !== type.phrase) || hits.find((h) => h.phrase === type.phrase) || type;
  if (!hits.length) return null;
  if (hits[0].value.skip || !SKIP_IN_MEALS.has(hits[0].value.slug)) return hits[0];
  // The best match is a cooking oil: another food wins if there is one, but not a word of the oil's own name
  // ("olive" in "olive oil").
  return hits.find((h) => !h.value.skip && !SKIP_IN_MEALS.has(h.value.slug) && !hits[0].phrase.includes(h.phrase)) || hits[0];
}

// Variants are shown as their base food with a small badge: almond milk is a glass of milk with an almond.
// [base, badge] by slug; a sticker that isn't uploaded yet is simply skipped.
const VARIANT_OF = {
  'almond-milk': ['milk', 'almonds'],
  'soy-milk': ['milk', 'edamame'],
  'oat-milk': ['milk', 'oats'],
  'chocolate-milk': ['milk', 'dark-chocolate'],
};
// Foods that read as a flavour or a variant on another product: fruit, nuts and seeds, and a few sweet flavours.
const FLAVOUR_SLUGS = new Set(['dark-chocolate', 'honey', 'date-syrup', 'maple-syrup', 'coffee', 'peanut-butter', 'almond-butter', 'tahini']);
const isFlavour = (slug) => {
  const st = bySlug.get(slug);
  return !!st && st.kind === 'food' && (st.category === 'fruit' || st.category === 'nuts-seeds' || FLAVOUR_SLUGS.has(slug));
};

// The flavour or variant named anywhere in the product (including "strawberry flavored"), other than the product itself.
function flavourBadge(name, main) {
  const n = normalize(name);
  const hit = FOOD_INDEX.find((e) => !e.value.skip && !e.value.group && e.value.slug !== main.slug
    && isFlavour(e.value.slug) && !(main.phrase || '').includes(e.phrase) && e.re.test(n));
  return hit ? bySlug.get(hit.value.slug) : null;
}

const withBadge = (sticker, badge) => {
  const out = withUrl(sticker);
  if (out && badge && badge.slug !== sticker.slug) out.badge = withUrl(badge);
  return out;
};

/**
 * The sticker for one ingredient name.
 * @returns {{ sticker, exact: boolean } | { skip: true } | null}
 *   exact — the food's own sticker; otherwise its food-group sticker (or the generic one).
 *   sticker.badge — a small second sticker for a variant or flavour (almond milk → milk + almond badge).
 * Looks at the name's main parts first (see mainParts), in order; extras like "with olive oil" never decide it.
 */
export function matchIngredient(name) {
  const parts = mainParts(name);
  if (!parts.length) return null;
  for (const part of parts) {
    const hit = bestHit(part);
    if (hit?.value.skip) return { skip: true };
    if (hit) {
      const variant = VARIANT_OF[hit.value.slug];
      if (variant && bySlug.has(variant[0])) {
        return { sticker: withBadge(bySlug.get(variant[0]), bySlug.get(variant[1])), exact: true };
      }
      const main = bySlug.get(hit.value.slug);
      return { sticker: withBadge(main, flavourBadge(name, { slug: hit.value.slug, phrase: hit.phrase })), exact: !hit.value.group };
    }
  }
  const cat = parts.map((part) => CATEGORY_INDEX.find((e) => e.re.test(part))).find(Boolean);
  return { sticker: withUrl(bySlug.get(cat ? cat.value.slug : 'category-generic')), exact: false };
}

// A container (bowl, glass, plate…) goes first: the tile tucks the toppings into it.
const containerFirst = (list) => {
  const i = list.findIndex((s) => s.vessel);
  return i > 0 ? [list[i], ...list.slice(0, i), ...list.slice(i + 1)] : list;
};

/**
 * Up to `max` stickers for a dish's ingredient list: its biggest foods by calories, skipping seasonings and
 * cooking fats. If no ingredient has its own sticker, the biggest one's food-group sticker stands alone.
 */
export function pickMealStickers(ingredients, max = 3) {
  const totals = new Map();
  let fallback = null;
  for (const ing of Array.isArray(ingredients) ? ingredients : []) {
    const m = matchIngredient(ing?.item || ing?.name);
    if (!m || m.skip || SKIP_IN_MEALS.has(m.sticker?.slug)) continue;
    const kcal = Number(ing?.calories) || 0;
    if (!m.exact) {
      if (!fallback || kcal > fallback.kcal) fallback = { sticker: m.sticker, kcal };
      continue;
    }
    const key = `${m.sticker.slug}|${m.sticker.badge?.slug || ''}`;
    const cur = totals.get(key) || { sticker: m.sticker, kcal: 0 };
    cur.kcal += kcal;
    totals.set(key, cur);
  }
  const picked = [...totals.values()].sort((a, b) => b.kcal - a.kcal).slice(0, max).map((t) => t.sticker);
  if (picked.length) return containerFirst(picked);
  return fallback ? [fallback.sticker] : [];
}

/**
 * Up to `max` stickers named in free text, in the order they appear — e.g. a dish hint
 * "Oatmeal topped with banana slices and almond butter." → oats, banana, almond butter.
 */
export function stickersFromText(text, max = 3) {
  let n = ` ${normalize(text)} `;
  const found = [];
  for (const e of FOOD_INDEX) {
    const re = new RegExp(e.re.source.replace('(?:^| )', ' '), 'g');
    let m;
    while ((m = re.exec(n))) {
      if (!e.value.skip && !e.value.group && !SKIP_IN_MEALS.has(e.value.slug)) found.push({ pos: m.index, slug: e.value.slug });
      // Blank out the match so a shorter phrase inside it ("almond" in "almond butter") can't match again.
      n = n.slice(0, m.index + 1) + ' '.repeat(m[0].length - 1) + n.slice(m.index + m[0].length);
      re.lastIndex = m.index + 1;
    }
  }
  const slugs = [...new Set(found.sort((a, b) => a.pos - b.pos).map((f) => f.slug))].slice(0, max);
  return containerFirst(slugs.map((s) => withUrl(bySlug.get(s))));
}
